/**
 * Customer Details Modal Component
 *
 * Displays comprehensive customer information in a dialog modal including:
 * - Basic profile (POPI compliant - limited data exposure)
 * - Order statistics (total orders, lifetime value)
 * - Dee Rewards standing (points, spend tier) from the native discount engine
 * - Payment methods used across all orders
 * - Recent orders with clickable navigation to order details
 *
 * **POPI Compliance:**
 * This component only displays data that the backend has already filtered
 * for POPI compliance. No sensitive data (passwords, tokens, etc.) is exposed.
 *
 * **Authorization:**
 * Access is controlled by the backend - only owner/admin roles can fetch customer data.
 *
 * @module UserManagement/CustomerDetailsModal
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Divider,
  Paper,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as DateIcon,
  ShoppingCart as OrdersIcon,
  AttachMoney as MoneyIcon,
  Calculate as AverageIcon,
  Stars as LoyaltyIcon,
  Payment as PaymentIcon,
  CreditCard as CardIcon,
  ShoppingCartCheckout as CartItemsIcon,
  Favorite as FavouritesIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import { useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import authInstance from '../../services/httpClient';
import { queryKeys } from '../../hooks/queries';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Customer details returned from the backend API.
 * All fields are POPI compliant - no sensitive data included.
 */
interface CustomerAddress {
  id: string;
  friendlyName: string;
  address: string;
  suburb: string;
  city: string;
  province: string;
  postalCode: string;
  isSelected: boolean;
  distanceKm: number | null;
  createdAt: string;
}

interface CustomerDetails {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  totalOrders: number;
  totalOrderValue: number;
  lastOrderAt: string | null;
  createdAt: string;
  /** Null when the loyalty standing could not be loaded (the rest of the profile still shows). */
  loyalty: {
    tier: string | null;
    points: number;
    hasCard: boolean;
    cardCode: string | null;
    pointsEarned: number;
    pointsPerReward: number | null;
    pointsToNextReward: number | null;
    tierNumber: number | null;
  } | null;
  recentOrders: Array<{
    id: string;
    orderNumber: string;
    totalPrice: number;
    status: string;
    createdAt: string;
    paymentMethod: string | null;
  }>;
  paymentMethodsUsed: string[];
  /**
   * Every store this customer has ordered from, busiest first — derived server-side
   * from their whole order history, not from `recentOrders`, which is capped.
   */
  storesOrderedFrom?: Array<{
    externalId: string;
    name: string;
    orderCount: number;
    lastOrderAt: string | null;
  }>;
  addresses: CustomerAddress[];
  cartItemsCount: number;
  favouritesCount: number;
  profile?: CustomerProfile | null;
}

/**
 * Enriched acquisition/device profile, built ~1h after signup.
 * LogRocket-derived fields are best-effort and may be null. Never includes the raw IP.
 */
interface CustomerProfile {
  geoNetwork: string | null;
  address: string | null;
  addressSuburb: string | null;
  addressCity: string | null;
  addressRegion: string | null;
  device: string | null;
  platform: string | null;
  /** "<Channel> · <content>" from Airbridge, e.g. "Website · banner_home". */
  source: string | null;
  likelySource: string | null;
  /** Which page drove the click, e.g. "Product — jack-parow-tjoef-10k". */
  sourcePage?: string | null;
  /** Search keyword, when the link that brought them carried one. */
  searchTerm?: string | null;
  /** Named campaign; null for the generic "app_install" stamp on our own links. */
  campaign?: string | null;
  phoneVerified: boolean;
  addressAdded: boolean;
  firstAddress: string | null;
  priorCustomer: boolean;
  inSession: string | null;
  status: string;
  builtAt: string | null;
}

/**
 * How each `customer_profiles.status` presents in the Profile header.
 *
 * `backfilled` is a terminal state used by the historical backfill
 * (`scripts/backfill_customer_profiles.mjs`): those customers signed up before
 * signup-time device/IP capture existed, so their blanks will never fill in and
 * the "still enriching" hint would be a lie. `partial`/`pending` DO still have a
 * build scheduled, so they keep it.
 */
const PROFILE_STATUS: Record<
  string,
  { label: string; color: 'success' | 'warning' | 'info' | 'default'; hint: string | null }
> = {
  complete: { label: 'Enriched', color: 'success', hint: null },
  partial: {
    label: 'Enriching',
    color: 'warning',
    hint: 'Enrichment in progress — some fields may still fill in.',
  },
  pending: {
    label: 'Queued',
    color: 'info',
    hint: 'The enriched profile is built about an hour after signup.',
  },
  backfilled: {
    label: 'From history',
    color: 'default',
    hint: 'Backfilled from existing records — this customer signed up before device and location capture was added, so those fields stay Unknown.',
  },
};

/**
 * The store an order is being fulfilled from.
 *
 * Only supplied when the modal is opened from an order — the Customers page has
 * no single order in hand, so it omits this and the section doesn't render.
 */
export interface OriginStore {
  name: string;
  code?: string | null;
  streetAddress?: string | null;
  locality?: string | null;
  pickupNotes?: string | null;
  /** Order this store is fulfilling, shown as context for the section. */
  orderNumber?: string | null;
}

/**
 * Props for the CustomerDetailsModal component.
 */
interface CustomerDetailsModalProps {
  /** Whether the modal is currently visible */
  open: boolean;
  /** UUID of the customer to display details for */
  customerId: string;
  /** Callback fired when the modal should be closed */
  onClose: () => void;
  /** Fulfilling store, when opened from an order detail page */
  originStore?: OriginStore | null;
}

export const CustomerDetailsModal = ({
  open,
  customerId,
  onClose,
  originStore = null,
}: CustomerDetailsModalProps) => {
  const notify = useNotify();
  const navigate = useNavigate();

  const { data: customer, isLoading: loading, error: queryError } = useQuery({
    queryKey: queryKeys.customers.detail(customerId),
    queryFn: async () => {
      const response = await authInstance.get(`admin/users/customers/${customerId}`);
      if (!response.data || typeof response.data !== 'object' || !response.data.customer) {
        throw new Error('Empty response from server — please refresh the page');
      }
      return response.data.customer as CustomerDetails;
    },
    enabled: open && !!customerId,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });

  const error = queryError ? (queryError instanceof Error ? queryError.message : 'Failed to load customer details') : null;

  // ============================================================================
  // FORMATTING HELPERS
  // ============================================================================

  /**
   * Formats a numeric amount as South African Rand (ZAR) currency.
   *
   * @param amount - The numeric amount to format
   * @returns Formatted currency string (e.g., "R 1,234.56")
   */
  const formatCurrency = (amount: number): string => {
    // Handle edge cases: NaN, null coerced to 0
    const safeAmount = Number.isFinite(amount) ? amount : 0;
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
    }).format(safeAmount);
  };

  /**
   * Formats a date string for display with date and time.
   * Returns "Never" for null/undefined dates (e.g., customers with no orders).
   *
   * @param dateString - ISO date string or null
   * @returns Formatted date string (e.g., "15 Jan 2026, 14:30") or "Never"
   */
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Never';

    const date = new Date(dateString);
    // Validate date is valid (handles malformed date strings)
    if (isNaN(date.getTime())) return 'Invalid date';

    return date.toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // ============================================================================
  // STATUS & DISPLAY HELPERS
  // ============================================================================

  /**
   * Maps order status to MUI chip color for visual indication.
   *
   * @param status - Order status string (e.g., "completed", "processing", "cancelled")
   * @returns MUI color prop value
   */
  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    // Defensive: handle undefined/null status
    if (!status) return 'default';

    switch (status.toLowerCase()) {
      case 'completed':
      case 'delivered':
        return 'success';
      case 'processing':
      case 'picking':
      case 'packed':
        return 'info';
      case 'dispatched':
      case 'tracking':
        return 'primary';
      case 'cancelled':
      case 'returned':
        return 'error';
      case 'placed':
        return 'warning';
      default:
        return 'default';
    }
  };

  /**
   * Maps payment gateway identifier to display label and color.
   * Used for both summary badges and order table payment column.
   *
   * **Supported Gateways:**
   * - paygate: Card payments via Paygate (primary)
   * - ozow: EFT/bank transfers (info)
   * - snapscan: SnapScan QR payments (warning)
   * - zapper: Zapper QR payments (secondary)
   * - payflex: Buy-now-pay-later (success)
   * - payjustnow: Buy-now-pay-later (error - red accent)
   *
   * @param method - Payment gateway identifier (lowercase in database)
   * @returns Object with label for display and color for MUI Chip
   */
  const getPaymentMethodInfo = (method: string): { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' } => {
    // Handle empty/null method - should not happen but defensive
    if (!method) return { label: 'Unknown', color: 'default' };

    const labels: Record<string, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }> = {
      paygate: { label: 'Paygate', color: 'primary' },
      ozow: { label: 'Ozow', color: 'info' },
      snapscan: { label: 'SnapScan', color: 'warning' },
      zapper: { label: 'Zapper', color: 'secondary' },
      payflex: { label: 'Payflex', color: 'success' },
      payjustnow: { label: 'PayJustNow', color: 'error' },
    };

    // Fallback: show original method name if not in mapping (future gateways)
    return labels[method.toLowerCase()] || { label: method, color: 'default' };
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Customer Details
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {customer && !loading && (
          <Box>
            {/* Basic Information */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="body2" fontWeight="bold">
                      {customer.firstName} {customer.lastName}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <EmailIcon fontSize="small" color="action" />
                    <Typography variant="body2">{customer.email}</Typography>
                    <Chip
                      label={customer.emailVerified ? 'Verified' : 'Unverified'}
                      size="small"
                      color={customer.emailVerified ? 'success' : 'default'}
                      variant={customer.emailVerified ? 'filled' : 'outlined'}
                      sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2">{customer.phoneNumber}</Typography>
                    <Chip
                      label={customer.phoneVerified ? 'Verified' : 'Unverified'}
                      size="small"
                      color={customer.phoneVerified ? 'success' : 'default'}
                      variant={customer.phoneVerified ? 'filled' : 'outlined'}
                      sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <DateIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      Joined: {formatDate(customer.createdAt)}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <DateIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      Last Order: {formatDate(customer.lastOrderAt)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Origin Base — only when opened from an order */}
            {originStore && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Origin Base
                </Typography>
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <StoreIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography variant="body2" fontWeight="bold">
                        {originStore.name}
                      </Typography>
                      {originStore.code && (
                        <Chip
                          label={originStore.code}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.65rem', fontFamily: 'monospace' }}
                        />
                      )}
                      {originStore.orderNumber && (
                        <Typography variant="caption" color="text.secondary">
                          fulfilling order #{originStore.orderNumber}
                        </Typography>
                      )}
                    </Box>
                    {originStore.streetAddress && (
                      <Typography variant="body2" color="text.secondary">
                        {originStore.streetAddress}
                      </Typography>
                    )}
                    {originStore.locality && (
                      <Typography variant="body2" color="text.secondary">
                        {originStore.locality}
                      </Typography>
                    )}
                    {originStore.pickupNotes && (
                      <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                        Pickup notes: &ldquo;{originStore.pickupNotes}&rdquo;
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Paper>
            )}

            {/* Order Statistics */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Order Statistics
              </Typography>
              <Grid container spacing={2}>
                <Grid size={4}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <OrdersIcon color="primary" />
                    <Box>
                      <Typography variant="h5">{customer.totalOrders}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Orders
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid size={4}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <MoneyIcon color="success" />
                    <Box>
                      <Typography variant="h5">
                        {formatCurrency(customer.totalOrderValue)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Lifetime Value
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid size={4}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <AverageIcon color="info" />
                    <Box>
                      <Typography variant="h5">
                        {formatCurrency(
                          customer.totalOrders > 0
                            ? customer.totalOrderValue / customer.totalOrders
                            : 0,
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Average Order
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Products */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Products
              </Typography>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CartItemsIcon color="primary" />
                    <Box>
                      <Typography variant="h5">{customer.cartItemsCount ?? 0}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Items in cart
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid size={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <FavouritesIcon color="error" />
                    <Box>
                      <Typography variant="h5">{customer.favouritesCount ?? 0}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Favourites
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Loyalty: the native discount engine's figures, the same as Marketing → Discounts → Customers */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                <Typography variant="h6" gutterBottom>
                  Dee Rewards
                </Typography>
                <Button
                  size="small"
                  onClick={() => {
                    onClose();
                    // DISCOUNTS_BASE lives in pages/Discounts; not imported so this modal doesn't pull that page in
                    navigate(`/discounts/customers/${customer.id}`);
                  }}
                >
                  Open in Discounts
                </Button>
              </Box>
              {!customer.loyalty ? (
                <Typography variant="body2" color="text.secondary">
                  Loyalty standing could not be loaded.
                </Typography>
              ) : !customer.loyalty.hasCard ? (
                <Typography variant="body2" color="text.secondary">
                  No loyalty card yet.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  <Grid size={4}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LoyaltyIcon color="warning" />
                      <Box>
                        <Chip label={customer.loyalty.tier} color="primary" size="small" />
                        <Typography variant="caption" display="block" color="text.secondary">
                          {customer.loyalty.cardCode ?? 'Card'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid size={4}>
                    <Typography variant="h5">{customer.loyalty.points}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Points balance
                      {customer.loyalty.pointsToNextReward ? ` · ${customer.loyalty.pointsToNextReward} to next reward` : ''}
                    </Typography>
                  </Grid>
                  <Grid size={4}>
                    <Typography variant="h5">{customer.loyalty.pointsEarned}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Points earned (lifetime)
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Paper>

            {/* Payment Methods Used */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Payment Methods Used
              </Typography>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <PaymentIcon color="action" sx={{ mr: 1 }} />
                {customer.paymentMethodsUsed && customer.paymentMethodsUsed.length > 0 ? (
                  customer.paymentMethodsUsed.map((method) => {
                    const info = getPaymentMethodInfo(method);
                    return (
                      <Chip
                        key={method}
                        icon={<CardIcon />}
                        label={info.label}
                        color={info.color}
                        size="small"
                        variant="outlined"
                      />
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No payment methods recorded
                  </Typography>
                )}
              </Box>
            </Paper>

            {/* Saved Addresses */}
            {customer.addresses && customer.addresses.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Saved Addresses
                </Typography>
                {customer.addresses.map((addr) => (
                  <Box
                    key={addr.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={addr.isSelected ? 700 : 400}>
                        {addr.friendlyName}
                        {addr.isSelected && (
                          <Chip label="Active" size="small" color="primary" sx={{ ml: 1, height: 18, fontSize: '0.6rem' }} />
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[addr.address, addr.suburb, addr.city].filter(Boolean).join(', ')}
                      </Typography>
                    </Box>
                    {addr.distanceKm != null && (
                      <Chip label={`${addr.distanceKm} km`} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                    )}
                  </Box>
                ))}
              </Paper>
            )}

            {/* Recent Orders */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Recent Orders
            </Typography>
            {customer.recentOrders.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Order Number</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell>Payment</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customer.recentOrders.map((order) => {
                      const paymentInfo = getPaymentMethodInfo(order.paymentMethod || '');
                      return (
                        <TableRow
                          key={order.id}
                          hover
                          onClick={() => {
                            onClose();
                            navigate(`/orders/${order.id}`);
                          }}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>{order.orderNumber}</TableCell>
                          <TableCell>{formatDate(order.createdAt)}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(order.totalPrice)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={<CardIcon />}
                              label={paymentInfo.label}
                              color={paymentInfo.color}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={order.status}
                              color={getStatusColor(order.status)}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">No recent orders found</Alert>
            )}

            {/* Stores ordered from — usually one branch, occasionally a few */}
            {customer.storesOrderedFrom && customer.storesOrderedFrom.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mt: 3, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Stores ordered from
                </Typography>
                {customer.storesOrderedFrom.map((store) => (
                  <Box
                    key={store.externalId}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 1,
                      py: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                      <StoreIcon fontSize="small" color="action" />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          {store.name}
                        </Typography>
                        {store.lastOrderAt && (
                          <Typography variant="caption" color="text.secondary">
                            Last order: {formatDate(store.lastOrderAt)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Chip
                      label={`${store.orderCount} ${store.orderCount === 1 ? 'order' : 'orders'}`}
                      size="small"
                      variant="outlined"
                      sx={{ flexShrink: 0, fontSize: '0.7rem' }}
                    />
                  </Box>
                ))}
              </Paper>
            )}

            {/* Profile — acquisition & device intel. Built ~1h after signup for
                anyone who joined after the feature shipped; older customers were
                backfilled from what the DB and Cognito already knew.
                Emoji + chips deliberately mirror the customer_signed_up Discord
                card (webhook-endpoints/service.js) so the two read the same. */}
            {customer.profile &&
              (() => {
                const p = customer.profile;
                const known = (v: string | null) =>
                  !!v && v.trim() !== '' && v.trim().toLowerCase() !== 'unknown';
                const sourceKnown = known(p.source);
                // "Likely Source" is only an honest label when there IS a likely
                // source. The backend leaves both null unless the source is
                // actually evidenced (it no longer guesses one off the platform,
                // and never claims a referral/promo either way — those are entered
                // at checkout, not signup), so with nothing to show this reads
                // "Source — Unknown" rather than "Likely Source — Unknown".
                const likelyKnown = !sourceKnown && known(p.likelySource);
                const status = PROFILE_STATUS[p.status] ?? PROFILE_STATUS.partial;

                /** A boolean row renders as a chip; everything else as text. */
                type ProfileRow = {
                  icon: string;
                  label: string;
                  value: string;
                  bool?: boolean;
                  /** Long free text (the LogRocket narrative) gets its own block. */
                  block?: boolean;
                };

                const rows: ProfileRow[] = [
                  { icon: '📍', label: 'Geo & Network', value: p.geoNetwork || 'Unknown' },
                  { icon: '🏠', label: 'Address', value: p.address || 'Unknown' },
                  { icon: '📱', label: 'Device', value: p.device || 'Unknown' },
                  { icon: '💻', label: 'Platform', value: p.platform || 'Unknown' },
                  {
                    icon: '🧭',
                    label: likelyKnown ? 'Likely Source' : 'Source',
                    value: sourceKnown
                      ? (p.source as string)
                      : likelyKnown
                        ? (p.likelySource as string)
                        : 'Unknown',
                  },
                  // The detail behind the source. Each row appears only when
                  // Airbridge actually resolved that field — an absent page or
                  // keyword is genuinely unknown, and a row reading "Unknown"
                  // would imply we looked and found nothing to show.
                  ...(known(p.sourcePage ?? null)
                    ? [{ icon: '🔗', label: 'From page', value: p.sourcePage as string }]
                    : []),
                  ...(known(p.searchTerm ?? null)
                    ? [{ icon: '🔍', label: 'Search term', value: `“${p.searchTerm}”` }]
                    : []),
                  ...(known(p.campaign ?? null)
                    ? [{ icon: '📣', label: 'Campaign', value: p.campaign as string }]
                    : []),
                  { icon: '✅', label: 'Phone Verified', value: p.phoneVerified ? 'Yes' : 'No', bool: p.phoneVerified },
                  {
                    icon: '📮',
                    label: 'Address Added',
                    value: p.addressAdded ? (p.firstAddress ? `Yes: ${p.firstAddress}` : 'Yes') : 'No',
                    bool: p.addressAdded,
                  },
                  { icon: '🔁', label: 'Prior Customer', value: p.priorCustomer ? 'Yes' : 'No', bool: p.priorCustomer },
                  ...(p.inSession
                    ? [{ icon: '📝', label: 'In-session', value: p.inSession, block: true }]
                    : []),
                ];

                return (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, mt: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span aria-hidden>🔎</span> Profile
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        {p.builtAt && (
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(p.builtAt)}
                          </Typography>
                        )}
                        <Chip
                          label={status.label}
                          size="small"
                          color={status.color}
                          variant={status.color === 'default' ? 'outlined' : 'filled'}
                          sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
                        />
                      </Box>
                    </Box>

                    {status.hint && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 1.5 }}
                      >
                        {status.hint}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {rows.map((row) => {
                        const unknown = row.value === 'Unknown';
                        return (
                          <Box
                            key={row.label}
                            sx={{
                              display: 'flex',
                              flexDirection: row.block ? 'column' : 'row',
                              gap: row.block ? 0.5 : 1,
                              alignItems: row.block ? 'stretch' : 'flex-start',
                              py: 0.5,
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              '&:last-child': { borderBottom: 'none' },
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                minWidth: 168,
                                flexShrink: 0,
                                color: 'text.secondary',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                              }}
                            >
                              <span aria-hidden style={{ fontSize: '0.95rem', lineHeight: 1 }}>
                                {row.icon}
                              </span>
                              {row.label}
                            </Typography>

                            {row.bool !== undefined ? (
                              <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                                <Chip
                                  label={row.bool ? 'Yes' : 'No'}
                                  size="small"
                                  color={row.bool ? 'success' : 'default'}
                                  variant={row.bool ? 'filled' : 'outlined'}
                                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
                                />
                                {/* "Yes: 5 Rowan Avenue…" — keep the address beside the chip */}
                                {row.bool && row.value.startsWith('Yes: ') && (
                                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                    {row.value.slice(5)}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{
                                  wordBreak: 'break-word',
                                  fontStyle: unknown ? 'italic' : 'normal',
                                  color: unknown ? 'text.disabled' : 'text.primary',
                                }}
                              >
                                {unknown ? '— Unknown' : row.value}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Paper>
                );
              })()}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
